/**
 * Form Display Names — Single Source of Truth
 *
 * Every module that needs to display a list of user forms (QR Generator,
 * Form Responses, Automations) MUST use these helpers so names stay
 * consistent across the entire app.
 *
 * Rules:
 *  - Portfolio → displayName || customDomain || "Main Website"
 *  - Property  → address || "Untitled Property"
 *  - No prefixes ("Lead Form –", "Portfolio Form") in the canonical name.
 *    If form *type* needs to be shown, render it as a separate badge.
 */

// ─── Name helpers ────────────────────────────────────────────────

/**
 * Canonical display name for the portfolio form.
 * @param {Object} portfolioData - portfolio.data from usePortfolioData()
 * @returns {string}
 */
export function getPortfolioFormName(portfolioData) {
    if (!portfolioData) return 'Main Website';
    return (
        portfolioData.displayName
        || portfolioData.customDomain
        || 'Main Website'
    );
}

/**
 * Canonical display name for a property form.
 * @param {Object} property - A property document { address, address2, … }
 * @returns {string}
 */
export function getPropertyFormName(property) {
    if (!property) return 'Untitled Property';
    return property.address || 'Untitled Property';
}

// ─── Available-forms list builder ────────────────────────────────

/**
 * Build a unified list of selectable forms from portfolio + properties.
 * Used by QR Generator, Automations, and any future form selectors.
 *
 * @param {Object}  portfolio    - Return value of usePortfolioData()
 *                                 Expected shape: { data, docId }
 * @param {Array}   properties   - Array of property documents from usePropertyData()
 * @returns {Array<{ id: string, name: string, type: 'portfolio'|'property', propertyId?: string }>}
 */
export function buildAvailableForms(portfolio, properties) {
    const forms = [];

    // Portfolio form (single entry)
    if (portfolio?.data) {
        if (!portfolio.docId) {
            console.warn('[buildAvailableForms] portfolio.docId is missing — form ID will fall back to "portfolio-main" which will NOT match real form submissions. Ensure usePortfolioData() provides docId.');
        }
        forms.push({
            id: `portfolio-${portfolio.docId || 'main'}`,
            name: getPortfolioFormName(portfolio.data),
            type: 'portfolio',
        });
    }

    // Property forms
    if (Array.isArray(properties)) {
        properties.forEach((prop) => {
            forms.push({
                id: `property-${prop.id}`,
                name: getPropertyFormName(prop),
                type: 'property',
                propertyId: prop.id,
            });
        });
    }

    return forms;
}

// ─── Properties-map builder (for Form Responses tab) ─────────────

/**
 * Build a { propertyId → display name } lookup object.
 * Useful for the Form Responses table's "Source" column.
 *
 * @param {Array} properties - Array of property documents
 * @returns {Object}
 */
export function buildPropertiesMap(properties) {
    if (!Array.isArray(properties) || properties.length === 0) return {};
    return properties.reduce((map, prop) => {
        map[prop.id] = getPropertyFormName(prop);
        return map;
    }, {});
}
